import { useState } from "react";

import { useGetIdentity, useNotification, useTranslation } from "@refinedev/core";

import { Box, Card, CardContent, Stack, Typography } from "@mui/material";

import { VerificationCodeInput } from "@/components/auth";
import { CardButton, VerificationStatusChip } from "@/components/common";
import { PhoneInput } from "@/components/common";
import { useMfaResource } from "@/hooks/useMfaResource";
import type { IUser } from "@/types/types";

import { Breadcrumb } from "./Breadcrumb";

interface ChangePhonePanelProps {
  onVerified?: (phoneNumber: string) => void;
}

/**
 * Phone number management panel.
 * Allows users to update and verify their phone number.
 */
export const ChangePhonePanel: React.FC<ChangePhonePanelProps> = ({ onVerified }) => {
  const { translate } = useTranslation();
  const { open: openNotification } = useNotification();
  const { data: user, refetch: refetchIdentity } = useGetIdentity<IUser>();
  const { changePhone, verifyPhone } = useMfaResource();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Optimistic state: show updated phone/status immediately after verification
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);

  const currentPhone = verifiedPhone ?? user?.mfa?.phone_number;
  const isPhoneVerified = verifiedPhone ? true : (user?.mfa?.phoneVerified ?? false);

  const handleSubmitPhone = async () => {
    if (!phoneNumber.trim()) {
      return;
    }
    setErrorMessage("");
    setIsSubmitting(true);

    const result = await changePhone(phoneNumber);
    setIsSubmitting(false);

    if (result.success) {
      setCodeSent(true);
    } else {
      setErrorMessage(result.error || translate("pages.profile.mfa.errors.sendCodeFailed"));
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      return;
    }
    setErrorMessage("");
    setIsSubmitting(true);

    const result = await verifyPhone(verificationCode);
    setIsSubmitting(false);

    if (result.success) {
      // Optimistically update displayed phone immediately
      setVerifiedPhone(phoneNumber);

      openNotification?.({
        type: "success",
        message: translate("pages.profile.changePhone.verifySuccess"),
      });

      // Reset form
      setCodeSent(false);
      setPhoneNumber("");
      setVerificationCode("");

      if (onVerified) {
        onVerified(phoneNumber);
      } else {
        await refetchIdentity();
      }
    } else {
      setErrorMessage(result.error || translate("pages.profile.mfa.errors.invalidCode"));
    }
  };

  return (
    <Box>
      <Breadcrumb
        items={[
          { label: translate("pages.profile.sections.profileAndSecurity"), to: "/profile?section=profile-security" },
          { label: translate("pages.profile.options.changePhone") },
        ]}
      />

      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            {/* Current Phone Info */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" color="primary.dark">
                {translate("pages.profile.changePhone.currentPhone")}
              </Typography>
              <Typography variant="subtitle1" color="text.primary">
                {currentPhone || translate("pages.profile.changePhone.noPhone")}
              </Typography>
              {currentPhone && (
                <VerificationStatusChip
                  label={translate(
                    isPhoneVerified ? "pages.profile.changePhone.verified" : "pages.profile.changePhone.notVerified"
                  )}
                  size="small"
                  isVerified={isPhoneVerified}
                />
              )}
            </Stack>

            {/* Disclaimer */}
            <Typography variant="body2" color="text.disabled">
              {translate("pages.profile.changePhone.disclaimer")}
            </Typography>

            {!codeSent ? (
              <>
                {/* Phone Input */}
                <PhoneInput
                  label={translate("pages.profile.changePhone.newPhone")}
                  required
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  disabled={isSubmitting}
                />

                {errorMessage && (
                  <Typography variant="body2" color="error.dark" align="center">
                    {errorMessage}
                  </Typography>
                )}

                <CardButton onClick={handleSubmitPhone} disabled={!phoneNumber.trim() || isSubmitting}>
                  {translate(isSubmitting ? "pages.mfa.sending" : "pages.profile.changePhone.submit")}
                </CardButton>
              </>
            ) : (
              <>
                {/* Verification */}
                <Typography variant="body2" color="text.hint">
                  {translate("pages.profile.changePhone.codeSent")}
                </Typography>

                <VerificationCodeInput
                  value={verificationCode}
                  onChange={code => {
                    setVerificationCode(code);
                    setErrorMessage("");
                  }}
                  disabled={isSubmitting}
                />

                {errorMessage && (
                  <Typography variant="body2" color="error.dark" align="center">
                    {errorMessage}
                  </Typography>
                )}

                <CardButton onClick={handleVerify} disabled={verificationCode.length !== 6 || isSubmitting}>
                  {translate(isSubmitting ? "pages.mfa.verifying" : "pages.profile.changePhone.verify")}
                </CardButton>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
